import type {
  InputDescription,
  KeyValueConfig,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";

import { observer } from "mobx-react-lite";
import { JsonObject } from "../JsonObject/JsonObject";
import * as S from "./style";
import { ArrayObject } from "../ArrayObject/ArrayObject";
import type { ReactElement } from "react";
import { StringValue } from "../StringValue/StringValue";
import { NumberValue } from "../NumberValue/NumberValue";
import { BooleanValue } from "../BooleanValue/BooleanValue";
import { ReferenceValue } from "../ReferenceValue/ReferenceValue";
import type { ActionCandidate } from "../../../../../../../../../types/candidate";

export const KeyValue = observer<{
  description: InputDescription;
  keyValue?: KeyValueConfig;
  action?: ActionCandidate;
  onClick: () => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onlyStructure?: boolean;
  depth: number;
  index: number;
}>(
  ({
    keyValue,
    description,
    onClick,
    onlyStructure,
    onRemove,
    onEdit,
    depth,
    action,
    index,
  }) => {
    const getKey = (key: ValueConfig_V): string => {
      if (key.value.oneofKind === "string") {
        return key?.value.string;
      }
      if (key.value.oneofKind === "number") {
        return key?.value.number.toString();
      }
      if (key.value.oneofKind === "bool") {
        return key?.value.bool ? "true" : "false";
      }
      return "";
    };

    // A required field is marked in the structure list so the builder does not
    // have to open each one to see it.
    const labelFor = (key: ValueConfig_V): string =>
      `${getKey(key)}${keyValue?.required === true ? "*" : ""}`;

    const renderItem = (key: ValueConfig_V, i: number): ReactElement => {
      const value = keyValue?.value?.values[i];

      if (value?.value.oneofKind === "keyValue" || !value) {
        return (
          <JsonObject
            description={description}
            onClick={onClick}
            onRemove={onRemove}
            onEdit={onEdit}
            onlyStructure={onlyStructure}
            label={labelFor(key)}
            depth={depth}
            index={index}
            action={action}
            config={keyValue?.value}
          />
        );
      }
      if (value?.value.oneofKind === "list") {
        return (
          <ArrayObject
            description={description}
            onClick={onClick}
            onRemove={onRemove}
            onEdit={onEdit}
            onlyStructure={onlyStructure}
            input={value.value.list}
            label={labelFor(key)}
            depth={depth}
            index={index}
            action={action}
          />
        );
      }
      if (value?.value.oneofKind === "string") {
        return (
          <StringValue
            onlyStructure={onlyStructure}
            value={keyValue?.value}
            label={labelFor(key)}
            onRemove={onRemove}
            onEdit={onEdit}
            index={index}
            action={action}
          />
        );
      }
      if (value?.value.oneofKind === "number") {
        return (
          <NumberValue
            onlyStructure={onlyStructure}
            value={value}
            label={labelFor(key)}
            onRemove={onRemove}
            onEdit={onEdit}
            index={index}
          />
        );
      }
      if (value?.value.oneofKind === "bool") {
        return (
          <BooleanValue
            onlyStructure={onlyStructure}
            value={value}
            label={labelFor(key)}
            onRemove={onRemove}
            onEdit={onEdit}
            index={index}
          />
        );
      }
      if (value?.value.oneofKind === "reference") {
        return (
          <ReferenceValue
            value={value}
            config={keyValue?.value}
            onRemove={onRemove}
            onEdit={onEdit}
            index={index}
            label={labelFor(key)}
            action={action}
          />
        );
      }
      return <></>;
    };

    return (
      <S.Root>
        {keyValue?.key?.values.map((value, index) => {
          return renderItem(value, index);
        })}
      </S.Root>
    );
  },
);
